import Image from "next/image";
import Icon from "@components/Icon";
import MainDropDown from "@components/MainDropDown";
import NavigationBar from "@components/NavigationBar/NavigationBar";
import NavigationMenuModal from "@components/NavigationMenuModal";
import {
	colours,
	device,
	fonts,
	fontsLineHeight,
	getColours,
	laptopMediaQuery,
	mobileMediaQuery,
	richTextHelper,
} from "@helpers";
// import { Actioner, GTMActionKeys } from "services/actioner";
import useDeviceDetect from "@helpers/deviceDetect";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
// import { SpeedInsights } from "@vercel/speed-insights/next";
import Link from "next/link";
import { useRouter } from "next/router";
import type React from "react";
import { useEffect, useRef, useState } from "react";
// import Script from "next/script";
import styled from "styled-components";
import ScrollTracker from "@hooks/ScrollTracker";
import BrazeNotificationBanner from "./BrazeNotificationBanner";
import SearchBar from "@components/NavigationBar/SearchBar";
import { useBrazeContentCards } from "@hooks/useBrazeContentCards";
import { usePathname } from "next/navigation";
import { BrazeCanvasProvider } from "@contexts/BrazeCanvasContext";

type file = {
	url: string;
};
type Fields = {
	description: string;
	title: string;
	file: file;
};
type Logo = {
	[key: string]: Fields;
};

type SubNav = {
	link: string;
	name: string;
	order: number;
};
type NavigationList = {
	link: string;
	name: string;
	order: number;
	subNav: SubNav;
};

type Navigation = {
	navigation: NavigationList;
};
type NotificationBarFields = {
	backgroundColour: string;
	name: string;
	removeNegativeSpacingBelowModule: string;
	copy: [];
};
type NotificationBar = {
	fields: NotificationBarFields;
};
type LayoutProps = {
	slug: string;
	logo: Logo;
	notificationBarToggle: string;
	notificationBar: NotificationBar;
	buttonText: string;
	buttonColor: string;
	navigationList: Navigation;
	navigationBackgroundColor: string;
	navigationcontentcolor: string;
	promoCodeToApply?: string;
	children: React.ReactNode;
};
type ContainerProps = {
	showMenuModal: boolean;
	showMainDropdown: boolean;
};
type HeadlineButtonProps = {
	backgroundColor: string;
	cursorPointerOnDesktop: boolean;
};

const Banner = styled.div`
  background-color: ${colours.MIDNIGHT_BLUE};
  color: white;
  height: fit-content;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: var(--euclid-medium);
  padding: 10px 20px;
  p {
    text-align: center;
  }
  p,
  a {
    color: ${colours.WHITE};
    font-size: ${fonts.P};
	text-align: center;
	@media screen and (min-width: ${device.laptop}) {
		font-size: 15px;
  }
  }
  a:hover {
    color: ${colours.PEACH};
  }
`;

const Container = styled.div<ContainerProps>`
  position: relative;
  width: 100%;
  max-width: 2560px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  overflow: ${({ showMenuModal }) => showMenuModal && "hidden"};

  @media (max-width: ${device.laptopL}) {
    overflow: ${({ showMainDropdown }) => showMainDropdown && "hidden"};
  }
`;

const Logo = styled(Image)`
  
  cursor: pointer;
  ${laptopMediaQuery} {
    margin-right: 20px;
  }
  ${mobileMediaQuery} {
    width: 150px !important;
    height: auto !important;
  }
`;

const Nav = styled.div`
  background-color: #ffffff;
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  height: 56px;
  padding: 10px 2%;
  @media (min-width: 1176px) {
    padding: 10px 4%;
  }
`;
const HeadlineButton = styled.button<HeadlineButtonProps>`
  align-items: center;
  background-color: ${(props) => getColours(props.backgroundColor)};
  border-radius: 12px;
  border: none;
  box-sizing: border-box;
  color: ${(props) =>
		getColours(props.backgroundColor) === colours.MIDNIGHT_BLUE
			? colours.WHITE
			: colours.MIDNIGHT_BLUE} !important;
  display: flex;
  font-family: var(--euclid-medium);
  font-size: ${fonts.A};
  line-height: ${fontsLineHeight.A};
  justify-content: center;
  outline: none;
  padding: 13px 20px;
  min-width: fit-content;
  width: 100%;
  @media (min-width: ${device.desktop}) {
    padding: 13px 32px;
    min-width: 193px;
  }
  cursor: ${(props) =>
		props.cursorPointerOnDesktop ? "pointer !important" : "default"};

  @media (min-width: 428px) {
    width: fit-content;
  }
  ${mobileMediaQuery} {
    font-size: 14px !important;
    padding: 10px 16px;
  }
  &:focus-visible {
    outline: 2px solid ${colours.MIDNIGHT_BLUE};
    outline-offset: 2px;
  }

  :hover {
    cursor: ${(props) =>
			props.cursorPointerOnDesktop
				? "pointer !important"
				: "default !important"};
    opacity: 0.8;
    color: ${colours.WHITE};
  }
  :disabled {
    background-color: hsl(208, 8%, 90%);
  }
  :hover:enabled {
    opacity: 0.8;
  }
  :active {
    transition: 0.3s all;
    transform: translateY(3px);
    border: 1px solid transparent;
    color: ${colours.WHITE};
    opacity: 0.8;
  }

  @media (min-width: ${device.desktopS}) {
    transition: none !important;
    transform: translateY(0px) !important;
    cursor: none;
    border: 1px solid transparent;
    &:hover {
      cursor: default !important;
    }
  }
`;

const NavButtonGroup = styled.div<{ laptopAndDesktop: boolean }>`
  display: flex;
  align-items: center;
  height: max-content;
  margin-left: auto;
  min-width: 120px;
  gap: 5px;
  ${mobileMediaQuery} {
    gap: 5px;
  }
`;

const LaptopAndDesktopMenu = styled.button`
  display: flex;
  margin-left: auto;
  cursor: pointer;
  padding: 8px;
  background: none;
  border: none;
  min-width: auto;
  padding: 0px;

  &:focus-visible {
    outline: 2px solid ${colours.MIDNIGHT_BLUE};
    outline-offset: 2px;
    border-radius: 4px;
  }

  @media (min-width: ${device.tabletL}) {
    display: none;
  }
`;

const LaptopAndDesktopNavigation = styled.div`
  display: none;
  color: ${colours.MIDNIGHT_BLUE};
  @media (min-width: ${device.tabletL}) {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto;
  }
`;

const MobileAndTabletMenu = styled.button<{ MobileAndTablet: boolean }>`
  display: flex;
  margin-left: auto;
  cursor: pointer;
  background: none;
  border: none;
  min-width: auto;
  padding: 0px;
  
  &:focus-visible {
    outline: 2px solid ${colours.MIDNIGHT_BLUE};
    outline-offset: 2px;
    border-radius: 4px;
  }
	
  &:not(:focus-visible) {
	border: none !important;
    outline: none;
  }
`;

const Span = styled.span`
  & > div,
  & > section:first-of-type {
    margin-bottom: 30px;
  }

  & > div:nth-last-child(2) {
    margin-top: -30px;
  }
  & > div:nth-last-child(1) {
    margin-top: -30px;
  }
  & > div > div > p:last-child {
    padding-bottom: 0px;
  }
  @media (min-width: 768px) {
    & > div, 
	& > section:first-of-type {
      margin-bottom: 30px;
    }
    & > div:nth-last-child(2) {
      margin-top: -60px;
    }
    & > div:nth-last-child(1) {
      margin-top: -60px;
    }
  }
`;

const Layout = ({
	slug,
	logo,
	notificationBarToggle,
	notificationBar,
	buttonText,
	buttonColor,
	navigationList,
	navigationBackgroundColor,
	navigationcontentcolor,
	promoCodeToApply,
	children,
}: LayoutProps) => {
	const router = useRouter();
	const pathname = usePathname();

	// Reset per-page Braze canvas claims on navigation to ensure fresh claims on each page
	useEffect(() => {
		try {
			const w: any = window;
			if (w) {
				w.__brazeCanvasClaimsMap = new Map();
				w.__brazeCanvasOwnersMap = new Map();
				w.__brazeBannerClaimsMap = new Map();
				w.__brazeBannerOwnersMap = new Map();
				w.__brazeBannerIndexMap = new Map();
				w.__brazeBannerIndexOwnersMap = new Map();
			}
		} catch {}
	}, [pathname]);

	// Get Braze content cards to check for announcement banners

	const {
		cards,
		loading,
		isBrazeAvailable,
		getCardsByType,
		logCardClick,
		logCardImpression,
	} = useBrazeContentCards();
	const currentPath = router.asPath;
	const loggedImpressionCardIds = useRef<Set<string>>(new Set());

	// Helper function to extract dynamic content from Braze
	const extractDynamicContent = (content: string): string | null => {
		if (!content) return null;

		// Look for the "Dynamic:" section in the content
		const dynamicMatch = content.match(/Dynamic:\s*([^\r\n]+)/);
		if (dynamicMatch?.[1]) {
			return dynamicMatch[1].trim();
		}

		// If no dynamic content found, return the full content
		return content.trim();
	};

	// Find Braze announcement banner content for current page
	const getBrazeBannerContent = (): {
		text: string | null;
		url: string | null;
	} => {
		const matchingCard = cards.find((card) => {
			if (card.extras?.content_card_type !== "announcement_banner") {
				return false;
			}
			const pageNames = card.extras?.page_names;
			if (pageNames) {
				const pages = pageNames.split(",").map((p) => p.trim());
				const pageMatches = pages.some((page) => {
					// Exact match
					if (page === currentPath) return true;
					// Wildcard match for all pages
					if (page === "/*") return true;
					return false;
				});
				return pageMatches;
			}
			return false;
		});

		if (matchingCard) {
			const dynamicContent = extractDynamicContent(
				matchingCard.description || "",
			);
			return {
				text: dynamicContent,
				url: matchingCard.url || matchingCard.extras?.link || null,
			};
		}

		return { text: null, url: null };
	};

	const brazeBannerData = getBrazeBannerContent();
	// Only show Braze content if:
	// 1. Not loading AND
	// 2. Braze is available AND
	// 3. We actually have banner text AND
	// 4. We have cards (to prevent showing Contentful when cards haven't loaded yet)
	const showBrazeContent =
		isBrazeAvailable && !loading && brazeBannerData.text && cards.length > 0;

	// Log Braze Content Card impression when banner is shown
	useEffect(() => {
		if (showBrazeContent) {
			const matchingCard = cards.find((card) => {
				if (card.extras?.content_card_type !== "announcement_banner") {
					return false;
				}
				const pageNames = card.extras?.page_names;
				if (pageNames) {
					const pages = pageNames.split(",").map((p) => p.trim());
					return pages.some((page) => {
						if (page === currentPath) return true;
						if (page === "/*") return true;
						return false;
					});
				}
				return false;
			});

			if (matchingCard) {
				import("../utils/brazeContentCards").then(
					({ brazeContentCardsManager }) => {
						brazeContentCardsManager.logCardImpression(matchingCard.id);
					},
				);
			}
		}
	}, [showBrazeContent, cards, currentPath]);

	const LogoArray = logo?.fields;
	const [showMenuModal, setShowMenuModal] = useState(false);
	const [showMainDropdown, setShowMainDropdown] = useState(false);
	const [url, setUrl] = useState("");

	const { isMobile } = useDeviceDetect();
	const [mobile, setMobile] = useState(false);

	// Log impressions when Braze banners are rendered (once per card)
	useEffect(() => {
		const brazeContentCards = getCardsByType("nav_buy_now_cta");
		const uniqueBrazeContentCards = brazeContentCards.filter(
			(card, index, self) => index === self.findIndex((c) => c.id === card.id),
		);
		const activeCard = uniqueBrazeContentCards[0];
		if (
			activeCard?.id &&
			!loggedImpressionCardIds.current.has(activeCard?.id)
		) {
			logCardImpression(activeCard?.id);
			loggedImpressionCardIds.current.add(activeCard?.id);
		}
	}, []);

	useEffect(() => {
		setMobile(isMobile);
	}, []);

	useEffect(() => {
		setUrl(window.location.href);
	}, []);

	const navigateToPage = () => {
		const brazeContentCards = getCardsByType("nav_buy_now_cta");
		const uniqueBrazeContentCards = brazeContentCards.filter(
			(card, index, self) => index === self.findIndex((c) => c.id === card.id),
		);
		const activeCard = uniqueBrazeContentCards[0];

		const baseUrl = "/buy";
		let finalUrl = baseUrl;

		if (activeCard?.id) {
			logCardClick(activeCard?.id);
		}

		if (activeCard?.extras?.params) {
			finalUrl = `${baseUrl}?${activeCard.extras.params}`;
		} else if (typeof promoCodeToApply !== "undefined" && promoCodeToApply) {
			finalUrl = `${baseUrl}?rc=${promoCodeToApply}endrc`;
		}
		const currentSearchParams = new URLSearchParams(window.location.search);
		const targetUrl = new URL(finalUrl, window.location.origin);
		currentSearchParams.forEach((value, key) => {
			if (!targetUrl.searchParams.has(key)) {
				targetUrl.searchParams.set(key, value);
			}
		});
		window.location.href = targetUrl.pathname + targetUrl.search;
	};

	const loaderProp = ({ src }) => {
		return src;
	};
	const renderLogo = () => (
		<a href="/" aria-label="Click on felix Logo to go to homepage">
			<Logo
				className="clickable"
				src="/images/felixmobile_Logo_RGB_Secondary.svg"
				alt={LogoArray?.description}
				priority
				width={207}
				height={28}
				loader={loaderProp}
			/>
		</a>
	);

	const MobileAndTablet = isMobile;

	const laptopAndDesktop = !isMobile;
	const renderHeadlineButton = () =>
		buttonText && (
			<HeadlineButton
				cursorPointerOnDesktop={laptopAndDesktop}
				onClick={() => {
					mixpanelTrack("cta_button_clicked", {
						cta_position: "WEB - Navigation - Headline Button",
						cta_text: buttonText,
					});
					if (laptopAndDesktop) {
						navigateToPage();
					} else {
						setShowMainDropdown(!showMainDropdown);
					}
				}}
				data-gtm-cta-position={slug}
				data-gtm-cta-text={buttonText}
				backgroundColor={buttonColor}
				onMouseEnter={() => {
					mixpanelTrack("switchnow_navbar_button_hover", {
						CTA_location: slug,
					});
					if (!laptopAndDesktop) {
						setShowMainDropdown(true);
					}
				}}
			>
				{buttonText}
			</HeadlineButton>
		);

	const menuIconSizes = {
		iconMobile: "50",
		iconTablet: "50",
		iconLaptop: "60",
		iconDesktop: "60",
		iconDesktopLarge: "60",
	};

	const onMenuIconClick = () => {
		setShowMainDropdown(false);

		if (showMenuModal) setShowMenuModal(false);
		else setShowMenuModal(true);
	};

	const handleMenuButtonClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		onMenuIconClick();
	};

	const renderMenuIcon = () => {
		const handleKeyDown = (e: React.KeyboardEvent) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				onMenuIconClick();
			}
		};

		return (
			<>
				{!mobile && (
					<LaptopAndDesktopMenu
						tabIndex={0}
						aria-label="Open menu"
						onKeyDown={handleKeyDown}
						onClick={handleMenuButtonClick}
						id="Menu"
					>
						<Icon
							iconName="menu"
							iconSizes={menuIconSizes}
							alt="Menu"
							style={{ pointerEvents: "none" }}
						/>
					</LaptopAndDesktopMenu>
				)}
			</>
		);
	};
	const renderIcon = () => {
		const handleKeyDown = (e: React.KeyboardEvent) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				onMenuIconClick();
			}
		};

		return (
			<>
				{mobile ? (
					<MobileAndTabletMenu
						MobileAndTablet={MobileAndTablet}
						tabIndex={0}
						aria-label="Open menu"
						id="Menu"
						onKeyDown={handleKeyDown}
					>
						<Icon
							iconName="menu"
							iconSizes={menuIconSizes}
							onClick={onMenuIconClick}
							alt="Menu"
						/>
					</MobileAndTabletMenu>
				) : null}
			</>
		);
	};
	const renderNavButtonGroup = () => (
		<NavButtonGroup laptopAndDesktop={laptopAndDesktop}>
			{/* {renderSecondaryHeadlineButton()} */}
			{renderHeadlineButton()}
			{renderMenuIcon()}
			{renderIcon()}
		</NavButtonGroup>
	);

	// loading resolves before the first paint (via useLayoutEffect in the hook),
	// so this effectively decides Braze vs Contentful without any visible blur or flash.
	const showBlurredPlaceholder = loading;

	return (
		<>
			{notificationBarToggle === "On" && (
				<Banner
					data-testid="homeBanner"
					role="banner"
					style={{
						// Reserve space while loading to prevent layout shift
						minHeight: "40px",
						filter: showBlurredPlaceholder ? "blur(10px)" : "none",
					}}
				>
					{showBrazeContent ? (
						// Show Braze content if available
						brazeBannerData.url ? (
							<a
								href={brazeBannerData.url}
								target="_blank"
								rel="noopener noreferrer"
								style={{ textDecoration: "none", color: "inherit" }}
								onClick={() => {
									const matchingCard = cards.find((card) => {
										if (
											card.extras?.content_card_type !== "announcement_banner"
										) {
											return false;
										}
										const pageNames = card.extras?.page_names;
										if (pageNames) {
											const pages = pageNames.split(",").map((p) => p.trim());
											return pages.some((page) => {
												if (page === currentPath) return true;
												if (page === "/*") return true;
												return false;
											});
										}
										return false;
									});

									if (matchingCard) {
										import("../utils/brazeContentCards").then(
											({ brazeContentCardsManager }) => {
												brazeContentCardsManager.logCardClick(matchingCard.id);
											},
										);
									}
								}}
							>
								{brazeBannerData.text}
							</a>
						) : (
							brazeBannerData.text
						)
					) : (
						// Show Contentful content (blurred while loading)
						richTextHelper(notificationBar?.fields.copy)
					)}
				</Banner>
			)}

			<MainDropDown
				notificationBarToggle={notificationBarToggle}
				showMainDropdown={showMainDropdown}
				setShowMainDropdown={setShowMainDropdown}
			/>
			<BrazeCanvasProvider>
				<Container
					showMenuModal={showMenuModal}
					showMainDropdown={showMainDropdown}
					// biome-ignore lint/a11y/useSemanticElements: <explanation>
					role="main"
				>
					<Nav
						// biome-ignore lint/a11y/useSemanticElements: <explanation>
						role="navigation"
						data-testid={`navigation-${pathname || ""}`
							.toString()
							.toLowerCase()
							.replace(/\s+/g, "-")}
					>
						{renderLogo()}
						<LaptopAndDesktopNavigation>
							{!mobile ? (
								<NavigationBar
									ctaUrl={slug}
									navigationList={navigationList?.navigation}
									setShowMainDropdown={setShowMainDropdown}
								/>
							) : null}
						</LaptopAndDesktopNavigation>
						{renderNavButtonGroup()}
					</Nav>
					<Span onMouseEnter={() => setShowMainDropdown(false)}>
						{children}
					</Span>
					<ScrollTracker />
					<NavigationMenuModal
						showMenuModal={showMenuModal}
						setShowMenuModal={setShowMenuModal}
						navigationList={navigationList?.navigation}
						navigationBackgroundColor={navigationBackgroundColor}
						navigationcontentcolor={navigationcontentcolor}
						buttonText={buttonText}
						buttonColor={buttonColor}
						logo={logo}
					/>
					{/* <SpeedInsights /> */}
				</Container>
			</BrazeCanvasProvider>
		</>
	);
};

export default Layout;
